import { useState, useEffect, useRef } from "react";
import { Modal } from "../../ui/modal";
import { PaymentDetailsModal } from "./PaymentDetailsModal";
import { request } from "../../../utils";
import { PayLink, PaymentDetails } from "../types";
import { ChevronDownIcon } from "lucide-react";
import Spinner from "../../ui/Spinner/Spinner";
import Button from "../../ui/button/Button";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";
import { usePaymentLinkStore } from "../../../stores/usePaymentLinkStore";
import { PAYMENT_METHOD } from "../../../utils/constant";
import DatePicker from "react-datepicker";
import { PaymentLinkSelectMethod } from "./PaymentLinkSelectMethod";

interface UpdatePaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  payLink: PayLink | null;
  onUpdateSuccess: (updatedLink: PayLink) => void;
}

export function UpdatePaymentLinkModal({
  isOpen,
  onClose,
  payLink,
  onUpdateSuccess,
}: UpdatePaymentLinkModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // ---- Form states
  const [amount, setAmount] = useState("");
  const [displayMessage, setDisplayMessage] = useState<string>("");
  const [selectedCurrency, setSelectedCurrency] = useState<any>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(
    null,
  );
  const [payerEmail, setPayerEmail] = useState<string>("");
  const [payerName, setPayerName] = useState<string>("");
  const [receiveTo, setReceiveTo] = useState<string>("");
  const [expiresAtDate, setExpiresAtDate] = useState<Date | null>(null);
  const [currencySearch, setCurrencySearch] = useState("");

  const [showMethodTypeModal, setShowMethodTypeModal] = useState(false);

  // ---- Modal states
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  // const [paymentMethod, setPaymentMethod] = useState<"BANK_TRANSFER" | "MOBILE_MONEY" | "STABLE_CURRENCY">("BANK_TRANSFER");
  const [paymentMethod, setPaymentMethod] = useState<
    | "BANK_TRANSFER"
    | "MOBILE_MONEY"
    | "STABLE_CURRENCY"
    | "WECHAT_PAY"
    | "ALIPAY"
  >("BANK_TRANSFER");
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ---- Use stores
  const {
    getFilteredCurrencies,
    loadingCurrency,
    hasLoaded,
    loadCurrencies,
    getCurrencyByIso2,
  } = useCurrencyStore();

  const { updatePaymentLink } = usePaymentLinkStore();

  // ---- Get filtered currencies for Payment Links (exclude stablecoins)
  const paymentLinkCurrencies = getFilteredCurrencies({
    excludedCodes: [""],
  });

  // ---- Load currencies when modal opens
  useEffect(() => {
    if (isOpen && !hasLoaded && !loadingCurrency) {
      loadCurrencies();
    }
  }, [isOpen, hasLoaded, loadingCurrency, loadCurrencies]);



  // ---- Load states for existing payment link
  useEffect(() => {
    if (payLink && hasLoaded) {
      setAmount(payLink.amount.toString());
      setDisplayMessage(payLink.displayMessage || "");
      setPayerEmail(payLink.payerDetails?.email || "");
      setPayerName(payLink.payerDetails?.name || "");

      if (payLink.expiresAt) {
        const expiryDate = new Date(payLink.expiresAt);
        if (!isNaN(expiryDate.getTime())) {
          setExpiresAtDate(expiryDate);
        } else {
          const defaultExpiry = new Date(payLink.createdAt);
          defaultExpiry.setDate(defaultExpiry.getDate() + 30);
          setExpiresAtDate(defaultExpiry);
        }
      } else {
        const defaultExpiry = new Date(payLink.createdAt);
        defaultExpiry.setDate(defaultExpiry.getDate() + 30);
        setExpiresAtDate(defaultExpiry);
      }

      // ---- Find the currency from the store
      const currency = getCurrencyByIso2(payLink.currencyIso2);

      if (currency) {
        setSelectedCurrency(currency);
      } else {
        // ---- Fallback if currency not found
        setSelectedCurrency({
          code: payLink.currencyIso2,
          name: payLink.currencyIso2,
          symbol: payLink.currencyIso2,
          iso2: payLink.currencyIso2,
          flag: "",
          paymentMethod: {
            bankTransfer: true,
            mobileMoney: false,
            stableCurrency: false,
          },
        });
      }

      if (payLink.paymentDetails) {
        // Handle stable currency details
        if (payLink.paymentDetails.paymentMethod === "STABLE_CURRENCY") {
          setPaymentDetails({
            walletAddress: payLink.paymentDetails.walletAddress,
            network: payLink.paymentDetails.network,
            currency: payLink.paymentDetails.currency || payLink.currencyIso2,
            iso2: payLink.currencyIso2,
            paymentMethod: "STABLE_CURRENCY",
          });
          setReceiveTo(
            `${payLink.paymentDetails.walletAddress?.slice(0, 10)}...${payLink.paymentDetails.walletAddress?.slice(-10)} (${payLink.paymentDetails.network})`,
          );
        } else {
          // Handle traditional payment details
          setPaymentDetails({
            accountName: payLink.paymentDetails.accountName,
            accountNumber: payLink.paymentDetails.accountNumber,
            currency: payLink.paymentDetails.currency || payLink.currencyIso2,
            iso2: payLink.currencyIso2,
            paymentMethod:
              payLink.paymentDetails.paymentMethod ===
              PAYMENT_METHOD.MOBILE_MONEY
                ? PAYMENT_METHOD.MOBILE_MONEY
                : PAYMENT_METHOD.BANK_TRANSFER,
            providerName: payLink.paymentDetails.providerName,
            bankCode: payLink.paymentDetails.bankCode,
            network: payLink.paymentDetails.network,
          });
          setReceiveTo(payLink.paymentDetails.accountName);
        }
      }
      if (payLink.paymentDetails?.paymentMethod) {
        setPaymentMethod(payLink.paymentDetails.paymentMethod as any);
      }
    }
  }, [payLink, hasLoaded, getCurrencyByIso2]);

  // const handleReceiveToClick = () => {
  //   if (!selectedCurrency) return;

  //   // Check if it's a stable currency
  //   if (selectedCurrency.paymentMethod.stableCurrency) {
  //     setPaymentMethod("STABLE_CURRENCY");
  //     setShowPaymentDetailsModal(true);
  //     return;
  //   }

  //   // Determine available payment methods for traditional currencies
  //   const canBankTransfer = selectedCurrency.paymentMethod.bankTransfer;
  //   const canMobileMoney = selectedCurrency.paymentMethod.mobileMoney;

  //   if (canBankTransfer && canMobileMoney) {
  //     setShowPaymentDetailsModal(true);
  //   } else if (canBankTransfer) {
  //     setPaymentMethod("BANK_TRANSFER");
  //     setShowPaymentDetailsModal(true);
  //   } else if (canMobileMoney) {
  //     setPaymentMethod("MOBILE_MONEY");
  //     setShowPaymentDetailsModal(true);
  //   } else {
  //     setPaymentMethod("BANK_TRANSFER");
  //     setShowPaymentDetailsModal(true);
  //   }
  // };

    const handleReceiveToClick = () => {
  if (!selectedCurrency) return;
  setShowMethodTypeModal(true);
};

const handleMethodTypeSelect = (
  methodType: "WALLET" | "TRANSFER" | "STABLE_CURRENCY" | "WECHAT_PAY" | "ALIPAY"
) => {
  setShowMethodTypeModal(false);

  if (methodType === "TRANSFER") {
    setPaymentMethod(
      selectedCurrency?.paymentMethod?.mobileMoney && !selectedCurrency?.paymentMethod?.bankTransfer
        ? "MOBILE_MONEY"
        : "BANK_TRANSFER"
    );
    setShowPaymentDetailsModal(true);
  } else if (methodType === "STABLE_CURRENCY") {
    setPaymentMethod("STABLE_CURRENCY");
    setShowPaymentDetailsModal(true);
  } else if (methodType === "WECHAT_PAY") {
    setPaymentMethod("WECHAT_PAY");
    setShowPaymentDetailsModal(true);
  } else if (methodType === "ALIPAY") {
    setPaymentMethod("ALIPAY");
    setShowPaymentDetailsModal(true);
  }
};

  const handleSavePaymentDetails = (details: PaymentDetails) => {
    if (!selectedCurrency) return;

    const method = details.paymentMethod || paymentMethod;

    const baseDetails = {
      currency: selectedCurrency.code,
      iso2: selectedCurrency.iso2,
      paymentMethod: method,
    };

    let builtDetails: PaymentDetails;

    if (method === "STABLE_CURRENCY") {
      builtDetails = {
        ...baseDetails,
        walletAddress: details.walletAddress,
        network: details.network,
      } as PaymentDetails;

      setReceiveTo(
        `${details.walletAddress?.slice(0, 10)}...${details.walletAddress?.slice(-10)} (${details.network})`,
      );
    } else if (method === "MOBILE_MONEY") {
      builtDetails = {
        ...baseDetails,
        accountName: details.accountName,
        accountNumber: details.accountNumber,
        network: details.providerName ?? details.network,
        providerName: details.providerName,
      } as PaymentDetails;

      setReceiveTo(details.accountName!);
    } else if (method === "WECHAT_PAY" || method === "ALIPAY") {
      builtDetails = {
        ...baseDetails,
        phoneNumber: details.phoneNumber,
        accountName: details.accountName,
      } as PaymentDetails;

      setReceiveTo(details.accountName!);
    } else {
      // BANK_TRANSFER
      builtDetails = {
        ...baseDetails,
        accountName: details.accountName,
        accountNumber: details.accountNumber,
        bankCode: details.bankCode || "",
        providerName: details.providerName || details.bankCode || "",

        ...((selectedCurrency.iso2 === "GB" || selectedCurrency.code === "EUR") &&
  details.holderCity && details.holderStreet && details.holderPostalCode && {
  holderCity: details.holderCity,
  holderStreet: details.holderStreet,
  holderPostalCode: details.holderPostalCode,
}),
      } as PaymentDetails;

      setReceiveTo(details.accountName!);
    }

    setPaymentDetails(builtDetails);
    setPaymentMethod(method as any);
    setShowPaymentDetailsModal(false);
  };

  // ---- Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCurrencyDropdown(false);
        setCurrencySearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---- Handle currency selection
  const handleSelectCurrency = (currency: any) => {
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
  

    if (selectedCurrency?.code !== currency.code) {
      setPaymentDetails(null);
      setReceiveTo("");
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue = e.target.value.replace(/,/g, ""); // strip commas first

    // Allow only numbers and decimals
    if (/^\d*\.?\d*$/.test(rawValue) || rawValue === "") {
      setAmount(rawValue);
      setError("");
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const updatedDate = new Date(date);
      updatedDate.setHours(23, 59, 59, 999);
      setExpiresAtDate(updatedDate);
    } else {
      setExpiresAtDate(null);
    }
  };

  const handleUpdatePaymentLink = async () => {
    if (
      !payLink?._id ||
      !selectedCurrency ||
      !paymentDetails ||
      !payerEmail ||
      !payerName
    )
      return;

    const numericAmount = amount ? parseFloat(amount) : 0;

    if (isNaN(numericAmount) || numericAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    const expiresAt = expiresAtDate
      ? new Date(
          new Date(expiresAtDate).getFullYear(),
          new Date(expiresAtDate).getMonth(),
          new Date(expiresAtDate).getDate(),
          23,
          59,
          59,
        )
      : undefined;

    setIsUpdating(true);
    setError(null);

    try {
      const response = await request.patch({
        url: `/payment-link/${payLink._id}`,
        data: {
          amount: numericAmount,
          currencyIso2: selectedCurrency.iso2,
          displayMessage,
          expiresAt,
          payerDetails: {
            name: payerName,
            email: payerEmail,
          },
          paymentDetails: {
            ...paymentDetails,
            currency: selectedCurrency.code,
            ...(paymentDetails.paymentMethod === "STABLE_CURRENCY" && {
              network: paymentDetails.network,
              walletAddress: paymentDetails.walletAddress,
            }),
          },
        },
      });

      if (response.success) {
        const updatedPaymentLink = response.data as PayLink;
        updatePaymentLink(payLink._id, updatedPaymentLink);
        setSuccess("Payment link updated successfully");
        onUpdateSuccess(updatedPaymentLink);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.message || "Failed to update payment link");
        setTimeout(() => setError(null), 3000);
      }
    } catch (err: any) {
      console.error("Error updating payment link:", err);
      setError(err.message || "An unexpected error occurred");
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            Update Payment Link
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-lg dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30">
            {success}
          </div>
        )}

        {loadingCurrency && !hasLoaded ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 h-[300px] flex items-center justify-center">
            <Spinner className="h-10 w-10 text-blue-500" />
          </div>
        ) : (
          <div
            className="space-y-6"
            style={{ maxHeight: "calc(90vh - 100px)", overflowY: "auto" }}
          >
            {/* Currency and Amount Section */}
            <div className="grid grid-cols-1 gap-4">
              {/* Currency Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Currency
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 pr-10 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center justify-between"
                    onClick={() => {
  setShowCurrencyDropdown(!showCurrencyDropdown);
  if (!showCurrencyDropdown) setCurrencySearch("");
}}
                    disabled={loadingCurrency}
                  >
                    {selectedCurrency ? (
                      <div className="flex items-center">
                        {selectedCurrency.flag && (
                          <img
                            src={selectedCurrency.flag}
                            alt={selectedCurrency.code}
                            className="w-5 h-5 mr-2"
                          />
                        )}
                        <span>
                          {selectedCurrency.name} ({selectedCurrency.code})
                        </span>
                      </div>
                    ) : (
                      <span>
                        {loadingCurrency ? "Loading..." : "Select currency"}
                      </span>
                    )}
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  </button>
                  {showCurrencyDropdown && (
  <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border dark:border-gray-700">
    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
      <input
        type="text"
        autoFocus
        placeholder="Search currency..."
        value={currencySearch}
        onChange={(e) => setCurrencySearch(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="max-h-52 overflow-auto py-1">
      {(() => {
        const search = currencySearch.trim().toLowerCase();
        const filtered = paymentLinkCurrencies.filter(
          (c) =>
            !search ||
            c.name.toLowerCase().includes(search) ||
            c.code.toLowerCase().includes(search),
        );
        return filtered.length === 0 ? (
          <p className="px-4 py-2 text-sm text-gray-400">No results</p>
        ) : (
          filtered.map((currency) => (
            <div
              key={`${currency.code}-${currency.iso2}`}
              className={`flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                selectedCurrency?.code === currency.code
                  ? "bg-gray-100 dark:bg-gray-700"
                  : ""
              }`}
              onClick={() => {
                handleSelectCurrency(currency);
                setCurrencySearch("");
              }}
            >
              <img src={currency.flag} alt={currency.code} className="w-5 h-5 mr-2" />
              <span className="text-gray-700 dark:text-white">
                {currency.name} ({currency.code})
              </span>
            </div>
          ))
        );
      })()}
    </div>
  </div>
)}
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Amount
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl font-semibold min-w-[50px] whitespace-nowrap">
                    {selectedCurrency?.symbol || ""}
                  </div>
                  <input
                    type="text"
                    className={`w-full ${
                      (selectedCurrency?.symbol?.length || 0) > 1
                        ? "pl-16"
                        : "pl-10"
                    } pr-4 py-3 text-2xl font-semibold text-gray-900 bg-transparent border-b border-gray-200 focus:ring-0 focus:border-blue-500 dark:text-white dark:border-gray-700`}
                    placeholder="0.00"
                    value={
                      amount
                        ? new Intl.NumberFormat().format(Number(amount))
                        : ""
                    }
                    onChange={handleAmountChange}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Description
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                value={displayMessage}
                placeholder="Payment for services rendered"
                onChange={(e) => setDisplayMessage(e.target.value)}
              />
            </div>

            {/* Payer Details */}
            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Payer Details
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Payer Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Enter payer's name"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Payer Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Enter payer's email"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Expire At (Optional)
              </h4>

              <div className="relative">
                <DatePicker
                  selected={expiresAtDate}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  placeholderText="Select expiry date"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  dateFormat="yyyy-MM-dd"
                  isClearable
                />
              </div>
            </div>

            {/* Receiver Details Section */}
            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Receiver Details
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Receive to
                  </label>
                  <button
                    type="button"
                    onClick={handleReceiveToClick}
                    disabled={!selectedCurrency}
                    className={`w-full px-4 py-3 text-left text-gray-700 bg-gray-50 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
                      !selectedCurrency
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:hover:border-blue-500"
                    }`}
                  >
                    <span className="block truncate">
                      {paymentDetails?.paymentMethod === "STABLE_CURRENCY" &&
                      paymentDetails?.walletAddress
                        ? `${paymentDetails.walletAddress.slice(0, 10)}...${paymentDetails.walletAddress.slice(-10)} (${paymentDetails.network})`
                        : receiveTo || "Select receiving method"}
                    </span>
                    <ChevronDownIcon className="float-right h-5 w-5 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4 pb-2 bottom-0 bg-white dark:bg-gray-900 z-10">
              <div className="flex justify-end gap-3">
                <Button
                  size="sm"
                  onClick={onClose}
                  disabled={isUpdating}
                  className="px-6 py-3"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleUpdatePaymentLink}
                  disabled={
                    !selectedCurrency ||
                    !paymentDetails ||
                    !amount ||
                    isUpdating
                  }
                  className="px-6 py-3"
                >
                  {isUpdating ? "Updating..." : "Update Payment Link"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <PaymentLinkSelectMethod
  isOpen={showMethodTypeModal}
  onClose={() => setShowMethodTypeModal(false)}
  onSelect={handleMethodTypeSelect}
  title="Receive To"
  currency={selectedCurrency?.code || ""}
/>

      {/* Payment Details Modal */}
      {selectedCurrency && (
        <PaymentDetailsModal
          isOpen={showPaymentDetailsModal}
          onClose={() => setShowPaymentDetailsModal(false)}
          onSave={handleSavePaymentDetails}
            hasBankList={selectedCurrency?.hasBankList ?? false}
          currency={selectedCurrency.code}
          paymentMethod={paymentMethod}
          toCurrency={{
            code: selectedCurrency.code,
            iso2: selectedCurrency.iso2,
            name: selectedCurrency.name,
          }}
        />
      )}
    </Modal>
  );
}
